<template>
  <div class="product-center-container" style="margin-top: 90px;">
    <!-- 电脑端布局 -->
    <div class="pc-layout" v-if="!isMobile">
      <el-row :gutter="20">
        <!-- 左侧产品列表 -->
        <el-col :span="8">
          <div class="product-list">
            <h3 class="list-title">产品分类</h3>
            <div v-if="loading.left" class="loading-container">
              <el-skeleton :rows="8" animated />
            </div>
            <el-scrollbar v-else class="scrollbar">
              <el-menu
                  :default-active="activeProduct"
                  class="product-menu"
              >
                <el-menu-item
                    v-for="product in leftProducts"
                    :key="product.id"
                    :index="product.id"
                    class="product-item"
                    @click="handleProductSelect(product.id)"
                >
                  <template #title>
                    <div class="menu-item-content">
                      <span class="product-name">{{ product.name }}</span>
                    </div>
                  </template>
                </el-menu-item>
              </el-menu>
            </el-scrollbar>
          </div>
        </el-col>

        <!-- 右侧产品展示 -->
        <el-col :span="16">
          <div class="products-display">
            <h2 class="section-title">{{ currentProductName}} 相关产品</h2>
            <div v-if="loading.right" class="loading-container">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else>
              <el-row :gutter="20">
                <el-col
                    v-for="product in allProducts"
                    :key="product.id"
                    :span="12"
                    class="product-col"
                >
                  <div class="product-card">
                    <h3 class="product-name">{{ product.name }}</h3>
                    <el-image
                        class="product-image"
                        :src="product.imgUrl"
                        fit="cover"
                        :alt="product.name"
                    >
                      <template #error>
                        <div class="image-slot">
                          <el-icon><Picture /></el-icon>
                        </div>
                      </template>
                    </el-image>
                    <div class="product-description">
                      <p>{{ product.label || '暂无描述' }}</p>
                    </div>
                    <el-button
                        type="primary"
                        class="learn-more-btn"
                        @click="goToProductDetail(product.id)"
                    >
                      了解更多
                    </el-button>
                  </div>
                </el-col>
              </el-row>

              <!-- 无数据提示 -->
              <div v-if="allProducts.length === 0" class="no-products">
                <el-empty description="该分类下暂无产品" />
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 手机端布局 -->
    <div class="mobile-layout" v-else>
      <!-- 左侧产品列表（顶部） -->
      <div class="mobile-product-list">
        <h3 class="mobile-list-title">产品系列</h3>
        <div v-if="loading.left" class="mobile-loading">
          <el-skeleton :rows="2" animated />
        </div>
        <el-scrollbar v-else class="mobile-scrollbar" tag="div" wrap-class="scrollbar-wrapper">
          <div class="mobile-product-scroll">
            <div
                v-for="product in leftProducts"
                :key="product.id"
                class="mobile-product-item"
                :class="{ active: activeProduct === product.id }"
                @click="handleProductSelect(product.id)"
            >
              <el-image
                  class="mobile-menu-item-image"
                  :src="product.imgUrl"
                  fit="cover"
                  :alt="product.name"
              >
                <template #error>
                  <div class="image-slot">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
              <span class="mobile-product-name">{{ product.name }}</span>
            </div>
          </div>
        </el-scrollbar>
      </div>

      <!-- 右侧产品展示（底部） -->
      <div class="mobile-products-display">
        <h2 class="mobile-section-title">{{ currentProductName || '产品' }} 相关产品</h2>
        <div v-if="loading.right" class="mobile-loading">
          <el-skeleton :rows="4" animated />
        </div>
        <div v-else class="mobile-products-grid">
          <div
              v-for="product in allProducts"
              :key="product.id"
              class="mobile-product-card"
          >
            <h3 class="mobile-product-name">{{ product.name }}</h3>
            <el-image
                class="mobile-product-image"
                :src="product.imgUrl"
                fit="cover"
                :alt="product.name"
            >
              <template #error>
                <div class="image-slot">
                  <el-icon><Picture /></el-icon>
                </div>
              </template>
            </el-image>
            <div class="mobile-product-description">
              <p>{{ product.label || '暂无描述' }}</p>
            </div>
            <el-button
                type="primary"
                size="small"
                class="mobile-learn-more-btn"
                @click="goToProductDetail(product.id)"
            >
              了解更多
            </el-button>
          </div>
        </div>

        <!-- 无数据提示 -->
        <div v-if="!loading.right && allProducts.length === 0" class="mobile-no-products">
          <el-empty description="该分类下暂无产品" :image-size="80" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import serverApi from '../../api/server';
import { Picture } from '@element-plus/icons-vue';

export default {
  name: 'ProductCenter',
  components: {
    Picture
  },
  data() {
    return {
      activeProduct: '7', // 默认选中产品ID
      windowWidth: 0,
      leftProducts: [], // 左侧产品分类
      allProducts: [],  // 右侧产品列表
      loading: {
        left: true,   // 左侧分类加载状态
        right: true   // 右侧产品加载状态
      }
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    currentProductName() {
      const product = this.leftProducts.find(item => item.id === this.activeProduct);
      return product ? product.name : '';
    }
  },
  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  created() {
    this.initProductData();
  },
  methods: {
    // 初始化产品数据
    async initProductData() {
      await this.loadProductClasses();
      this.handleRouteParams();
    },

    // 加载产品分类
    async loadProductClasses() {
      this.loading.left = true;
      try {
        const res = await serverApi.getProductClassNameList();
        this.leftProducts = res.data.data || [];

        // 如果没有默认选中的分类，选择第一个
        if (this.leftProducts.length > 0 && !this.$route.query.id) {
          this.activeProduct = this.leftProducts[0].id;
        }
      } catch (error) {
        console.error('加载产品分类失败:', error);
        this.leftProducts = [];
      } finally {
        this.loading.left = false;
      }
    },

    // 处理路由参数
    handleRouteParams() {
      // 检查路由中是否有 id 参数
      if (this.$route.query.id) {
        // 只有当当前选中项与路由中的 id 不一致时才更新
        if (this.activeProduct !== this.$route.query.id) {
          this.handleProductSelect(this.$route.query.id);
        }
      } else {
        this.handleProductSelect(this.activeProduct);
      }
    },

    // 窗口大小变化处理
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    // 产品分类选择处理
    handleProductSelect(id) {
      // 验证ID有效性
      if (!id) {
        console.warn('无效的产品分类ID');
        return;
      }

      this.activeProduct = id;
      this.loadProducts(id);
      this.updateRoute(id);
    },

    // 加载产品列表
    async loadProducts(id) {
      // 验证ID有效性
      if (!id) {
        console.warn('无效的产品分类ID');
        this.allProducts = [];
        return;
      }

      this.loading.right = true;
      try {
        const res = await serverApi.getProductByClassId(id);
        this.allProducts = res.data.data || [];
      } catch (error) {
        console.error('加载产品列表失败:', error);
        this.allProducts = [];
      } finally {
        this.loading.right = false;
      }
    },

    // 更新路由参数
    updateRoute(id) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          id: id,
          number: 1
        }
      }).catch(() => {
      });
    },

    // 跳转到产品详情
    goToProductDetail(id) {
      // 验证ID有效性
      if (!id) {
        console.warn('无效的产品ID');
        return;
      }

      this.$router.push({
        path: '/productContent', // 使用绝对路径
        query: {id: id}
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err);
        }
      });
    }
  },
  // 添加路由监听
  watch: {
    '$route'(to, from) {
      // 当路由变化时，检查是否是产品相关的查询参数变化
      if (to.name === 'Product' && to.query.id) {
        this.handleRouteParams();
      }
    }
  }
}
</script>

<style scoped>
/* 通用样式 */
.product-center-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.loading-container, .mobile-loading {
  padding: 20px 0;
}

.no-products, .mobile-no-products {
  padding: 40px 0;
  text-align: center;
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f5f7fa;
  color: #909399;
}

.image-slot .el-icon {
  font-size: 24px;
}

/* 电脑端样式 */
.pc-layout .product-list {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.pc-layout .list-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  font-weight: bold;
}

.pc-layout .scrollbar {
  height: calc(100% - 50px);
}

.pc-layout .product-menu {
  border-right: none;
}

.pc-layout .product-item {
  height: 80px;
  line-height: 80px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.pc-layout .product-item:hover {
  background-color: #f5f7fa;
}

.pc-layout .product-item:last-child {
  border-bottom: none;
}

.pc-layout .menu-item-content {
  display: flex;
  align-items: center;
}

.pc-layout .menu-item-image {
  width: 60px;
  height: 60px;
  margin-right: 15px;
  border-radius: 4px;
}

.pc-layout .product-name {
  font-size: 16px;
  font-weight: 500;
}

.pc-layout .products-display {
  background: #fff;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  min-height: 600px;
}

.pc-layout .section-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  font-weight: bold;
}

.pc-layout .product-col {
  margin-bottom: 30px;
}

.pc-layout .product-card {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;
  transition: all 0.3s;
}

.pc-layout .product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.pc-layout .product-name {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  font-weight: bold;
}

.pc-layout .product-image {
  width: 100%;
  height: 200px;
  margin-bottom: 15px;
  border-radius: 6px;
  object-fit: cover;
}

.pc-layout .product-description {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  flex-grow: 1;
}

.pc-layout .learn-more-btn {
  width: 100%;
  padding: 12px;
  font-size: 16px;
}

/* 手机端样式 */
.mobile-layout {
  display: flex;
  flex-direction: column;
  padding: 10px;
}

.mobile-product-list {
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 15px;
}

.mobile-list-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 12px;
  font-weight: bold;
}

.mobile-scrollbar {
  height: 120px;
}

.mobile-product-scroll {
  display: flex;
  padding-bottom: 10px;
}

.mobile-product-item {
  flex: 0 0 90px;
  margin-right: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f8f9fa;
}

.mobile-product-item.active {
  background-color: #e6f2ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.mobile-menu-item-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  margin-bottom: 5px;
}

.mobile-product-name {
  font-size: 12px;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-products-display {
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-section-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
  font-weight: bold;
}

.mobile-products-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.mobile-product-card {
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #eee;
}

.mobile-product-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  font-weight: bold;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-product-image {
  width: 100%;
  height: 100px;
  border-radius: 6px;
  margin-bottom: 10px;
  object-fit: cover;
}

.mobile-product-description {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-learn-more-btn {
  width: 100%;
  padding: 8px;
  font-size: 12px;
}

/* 响应式调整 */
@media (max-width: 480px) {
  .mobile-products-grid {
    grid-template-columns: 1fr;
  }

  .mobile-product-image {
    height: 150px;
  }
}
</style>
